import { useQuery } from "@tanstack/react-query";

import { getTimezone } from "@/lib/timezone";

import { DiscountDetail } from "@/types/billing/discount";
import { MemberTableFilters } from "@/types/inventory/member-collections";

import {
  getMemberIssuedDiscounts,
  getMemberPastDue,
  getMemberPayments,
  getSwitchPreview,
  listMembersTable,
} from "@/api/provider/inventory/member";
import { getMembershipsOverview } from "@/api/provider/inventory/membership";

export const memberQueryKeys = {
  all: ["inventory", "members"] as const,
  issuedDiscounts: (memberId: string) =>
    [...memberQueryKeys.all, "issued-discounts", memberId] as const,
  switchPreview: (memberId: string, newMembershipId: string) =>
    [
      ...memberQueryKeys.all,
      "switch-preview",
      memberId,
      newMembershipId,
    ] as const,
  // The request also carries the viewer's timezone (it decides which days a
  // next-charge range covers), so it is part of the key.
  table: (filters: MemberTableFilters) =>
    [...memberQueryKeys.all, "table", filters, getTimezone()] as const,
  payments: (memberId: string) =>
    [...memberQueryKeys.all, "payments", memberId] as const,
  pastDue: (memberId: string) =>
    [...memberQueryKeys.all, "past-due", memberId] as const,
  // Under `all` so anything that moves a member's money refreshes the totals.
  overview: () => [...memberQueryKeys.all, "overview"] as const,
};

export const memberQueryOptions = {
  issuedDiscounts: (memberId: string) => ({
    queryKey: memberQueryKeys.issuedDiscounts(memberId),
    queryFn: () => getMemberIssuedDiscounts(memberId),
    enabled: !!memberId,
    select: (data: DiscountDetail[]) => data,
    staleTime: 1000 * 60 * 2,
  }),
  switchPreview: (memberId: string, newMembershipId: string) => ({
    queryKey: memberQueryKeys.switchPreview(memberId, newMembershipId),
    queryFn: () => getSwitchPreview(memberId, newMembershipId),
    // Priced off the member's billing date as it stands right now, which staff
    // can change between opening the dialog twice.
    staleTime: 0,
  }),
  table: (filters: MemberTableFilters) => ({
    queryKey: memberQueryKeys.table(filters),
    queryFn: () => listMembersTable(filters),
  }),
  payments: (memberId: string) => ({
    queryKey: memberQueryKeys.payments(memberId),
    queryFn: () => getMemberPayments(memberId),
    enabled: !!memberId,
  }),
  pastDue: (memberId: string) => ({
    queryKey: memberQueryKeys.pastDue(memberId),
    queryFn: () => getMemberPastDue(memberId),
    enabled: !!memberId,
  }),
  overview: () => ({
    queryKey: memberQueryKeys.overview(),
    queryFn: () => getMembershipsOverview(),
  }),
};

export function useMemberIssuedDiscounts(
  memberId: string,
  { enabled = true }: { enabled?: boolean } = {},
) {
  const options = memberQueryOptions.issuedDiscounts(memberId);
  return useQuery({ ...options, enabled: options.enabled && enabled });
}
